import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatISO } from "date-fns/formatISO";
import { isValid } from "date-fns/isValid";

import { useToast } from "~components/shared/useToast.tsx";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import type { TeamAssignment } from "~generated";
import { EmployeesService, TeamAssignmentsService } from "~generated";
import type { QueryOpts } from "./helpers";
import { teamAssignmentKeys } from "./teamAssignmentKeys";

async function getCurrentEmployeeTeamAssignments(tenantId: string) {
  const teamAssignments =
    await EmployeesService.getCurrentEmployeeTeamAssignments(tenantId);
  if (!teamAssignments?.team_assignments) {
    return [];
  }

  return teamAssignments.team_assignments;
}

export function useCurrentEmployeeTeamAssignments(
  options?: QueryOpts<
    TeamAssignment[],
    ReturnType<typeof teamAssignmentKeys.me>
  >,
) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    ...options,
    queryKey: teamAssignmentKeys.me(currentTenant.id),
    queryFn: async () => getCurrentEmployeeTeamAssignments(currentTenant.id),
  });
}

export function useTeamAssignments({
  startDate,
  endDate,
}: {
  startDate?: Date | null;
  endDate?: Date | null;
} = {}) {
  const currentTenant = useCurrentTenant();

  const startDateISO =
    startDate && isValid(startDate)
      ? formatISO(startDate, { representation: "date" })
      : undefined;

  const endDateISO =
    endDate && isValid(endDate)
      ? formatISO(endDate, { representation: "date" })
      : undefined;

  const query = useQuery({
    queryKey: teamAssignmentKeys.list(currentTenant.id, {
      startDate: startDateISO,
      endDate: endDateISO,
    }),
    queryFn: async () => {
      const teamAssignments = await TeamAssignmentsService.getTeamAssignments(
        currentTenant.id,
        startDateISO,
        endDateISO,
      );
      if (!teamAssignments?.team_assignments) {
        return [];
      }
      return teamAssignments.team_assignments;
    },
  });

  return {
    ...query,
    data: query.data || [],
  };
}

export function useCurrentEmployeeTeamAssignment(id?: string) {
  const { data: teamAssignments, isLoading } =
    useCurrentEmployeeTeamAssignments();

  const data = (teamAssignments || []).find(
    ({ id: teamAssignmentId }) => id === teamAssignmentId,
  );

  return {
    data,
    isLoading,
  };
}

export function useUpdateTeamAssignment() {
  const currentTenant = useCurrentTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (teamAssignment: TeamAssignment) =>
      TeamAssignmentsService.updateTeamAssignment(
        currentTenant.id,
        teamAssignment.id as string,
        teamAssignment,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: teamAssignmentKeys.base(currentTenant.id),
      });
    },
  });
}

export function useDeleteTeamAssignment() {
  const { showSuccessToast, showErrorToast } = useToast();
  const t = useTranslation();
  const currentTenant = useCurrentTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamAssignmentId: string) =>
      TeamAssignmentsService.deleteTeamAssignment(
        currentTenant.id,
        teamAssignmentId,
      ),
    onSuccess: async () => {
      showSuccessToast(t.deleteTeamAssignmentSuccess);
      await queryClient.invalidateQueries({
        queryKey: teamAssignmentKeys.base(currentTenant.id),
      });
    },
    onError: () => {
      showErrorToast(t.deleteTeamAssignmentError);
    },
  });
}
